import type { MouseEvent, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';

import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Link, useLocation } from 'react-router';
import {
  Box,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@/shared/ui';

import { normalizeSx } from '@/shared/lib/normalizeSx';
import { Tag } from '@/shared/ui/primitives/Tag';

import { useNavigationContext } from '../../context/NavigationContext';
import { getNextOpenPaths, getSelectedHoverStyles, getSelectedStyles } from './openPaths';
import { useMenuCloseTimeout } from './useMenuCloseTimeout';
import { useNavigationItemState } from './useNavigationItemState';

import type { Sx } from '@/shared/types/uiTypes';

export interface NavigationItemData {
  key: string;
  id?: string;
  name?: string;
  icon?: ReactNode;
  path?: string;
  onClick?: () => void;
  children?: NavigationItemData[];
  tag?: string | null;
  alignIconWithTopLevel?: boolean;
  textSx?: Sx;
}

interface NavigationItemProps {
  item: NavigationItemData;
  level?: number;
  parentPath?: string;
}

const getPaddingLeftNumber = (level: number): number => (level === 0 ? 24 : 37 + 12 * level + 8);

const getAlignedChildOffset = (
  alignIconWithTopLevel: boolean | undefined,
  level: number,
  paddingLeftNumber: number
): number => (alignIconWithTopLevel && level > 0 ? paddingLeftNumber - 24 : 0);

const createActionItemClickHandler =
  (onClick: () => void, scheduleMenuClose: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    onClick();
    scheduleMenuClose();
  };

const getNavigationButtonProps = ({
  handleClick,
  handleNonRootItemClick,
  handleRootItemClick,
  hasChildren,
  item,
  level,
  scheduleMenuClose
}: {
  handleClick: (event: MouseEvent) => void;
  handleNonRootItemClick: (event: MouseEvent) => void;
  handleRootItemClick: (event: MouseEvent) => void;
  hasChildren: boolean;
  item: NavigationItemData;
  level: number;
  scheduleMenuClose: () => void;
}) => {
  if (hasChildren) return { onClick: handleClick };
  if (item.onClick)
    return { onClick: createActionItemClickHandler(item.onClick, scheduleMenuClose) };
  if (item.path) {
    return {
      component: Link,
      to: item.path,
      onClick: level === 0 ? handleRootItemClick : handleNonRootItemClick
    };
  }
  return { onClick: handleClick };
};

const NavigationItemIcon = ({
  alignIconWithTopLevel,
  icon,
  level,
  paddingLeftNumber
}: {
  alignIconWithTopLevel?: boolean;
  icon?: ReactNode;
  level: number;
  paddingLeftNumber: number;
}) => {
  if (!icon) return null;

  return (
    <ListItemIcon
      sx={{
        minWidth: 0,
        justifyContent: 'center',
        position: 'relative',
        right: getAlignedChildOffset(alignIconWithTopLevel, level, paddingLeftNumber)
      }}
    >
      {icon}
    </ListItemIcon>
  );
};

const NavigationItemTag = ({ level, tag }: { level: number; tag?: string | null }) => {
  if (!tag) return null;

  return (
    <Box
      sx={{
        marginLeft: level === 0 ? '16px' : 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Tag label={tag} />
    </Box>
  );
};

const NavigationItemExpandIcon = ({
  hasChildren,
  isMenuOpen,
  shouldBeOpen
}: {
  hasChildren: boolean;
  isMenuOpen: boolean;
  shouldBeOpen: boolean;
}) => {
  if (!hasChildren || !isMenuOpen) return null;

  const ExpandIcon = shouldBeOpen ? ExpandLess : ExpandMore;
  return <ExpandIcon sx={{ mb: '-3px' }} />;
};

const NavigationItemPrimary = ({
  hasChildren,
  isMenuOpen,
  isSelected,
  item,
  level,
  paddingLeftNumber,
  shouldBeOpen
}: {
  hasChildren: boolean;
  isMenuOpen: boolean;
  isSelected: boolean;
  item: NavigationItemData;
  level: number;
  paddingLeftNumber: number;
  shouldBeOpen: boolean;
}) => (
  <Box
    sx={{
      display: isMenuOpen ? 'flex' : 'none',
      alignItems: 'center',
      fontWeight: isSelected ? 600 : 500,
      gap: '8px',
      position: 'relative',
      right: getAlignedChildOffset(item.alignIconWithTopLevel, level, paddingLeftNumber)
    }}
  >
    <Typography
      noWrap
      sx={[
        {
          fontSize: level > 0 ? '14px' : '16px',
          display: 'inline-flex',
          overflow: 'hidden'
        },
        ...normalizeSx(item.textSx)
      ]}
    >
      {item.name}
    </Typography>
    <NavigationItemTag
      level={level}
      tag={item.tag}
    />
    <NavigationItemExpandIcon
      hasChildren={hasChildren}
      isMenuOpen={isMenuOpen}
      shouldBeOpen={shouldBeOpen}
    />
  </Box>
);

const NavigationItemChildren = ({
  fullPath,
  hasChildren,
  isMenuOpen,
  items,
  level,
  shouldBeOpen
}: {
  fullPath: string;
  hasChildren: boolean;
  isMenuOpen: boolean;
  items?: NavigationItemData[];
  level: number;
  shouldBeOpen: boolean;
}) => {
  if (!isMenuOpen || !hasChildren) return null;

  return (
    <Collapse
      unmountOnExit
      in={shouldBeOpen}
      timeout='auto'
    >
      <List
        disablePadding
        component='div'
      >
        {items?.map(childItem => (
          <NavigationItem
            key={childItem.key}
            item={childItem}
            level={level + 1}
            parentPath={fullPath}
          />
        ))}
      </List>
    </Collapse>
  );
};

const NavigationItem = ({ item, parentPath = '', level = 0 }: NavigationItemProps) => {
  const location = useLocation();
  const { setOpenPaths, isMenuOpen, setOpenMenu, close } = useNavigationContext();
  const { fullPath, hasChildren, shouldBeOpen, isSelected } = useNavigationItemState(
    item,
    parentPath,
    level
  );
  const { scheduleClose } = useMenuCloseTimeout(() => {
    setOpenMenu(false);
  });
  const { scheduleClose: scheduleMenuClose } = useMenuCloseTimeout(close);

  const paddingLeftNumber = getPaddingLeftNumber(level);
  const paddingLeft = `${paddingLeftNumber}px`;

  const handleClick = useCallback(() => {
    if (level === 0 && !isMenuOpen) {
      setOpenMenu(true);
    }
    setOpenPaths(prev =>
      getNextOpenPaths(prev, level, fullPath, { isMenuOpen, isCurrentlyOpen: shouldBeOpen })
    );
  }, [level, setOpenPaths, isMenuOpen, setOpenMenu, fullPath, shouldBeOpen]);

  const handleNonRootItemClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();

      if (location.pathname === fullPath) {
        e.preventDefault();
        setOpenMenu(true);
        return;
      }

      scheduleClose();
    },
    [setOpenMenu, location.pathname, fullPath, scheduleClose]
  );

  const handleRootItemClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      setOpenMenu(true);
      scheduleClose();
    },
    [setOpenMenu, scheduleClose]
  );

  const buttonProps = useMemo(
    () =>
      getNavigationButtonProps({
        handleClick,
        handleNonRootItemClick,
        handleRootItemClick,
        hasChildren,
        item,
        level,
        scheduleMenuClose
      }),
    [
      handleClick,
      handleNonRootItemClick,
      handleRootItemClick,
      hasChildren,
      item,
      level,
      scheduleMenuClose
    ]
  );

  return (
    <ListItem
      disablePadding
      component='div'
      sx={{
        display: 'block',
        ...(level === 0 && { '&:last-child': { mt: 'auto', mb: 0 } }),
        '.MuiListItemButton-root': shouldBeOpen
          ? {
              backgroundColor: 'gx.tileLight'
            }
          : {}
      }}
    >
      <ListItemButton
        selected={isSelected || shouldBeOpen}
        {...buttonProps}
        sx={{
          gap: '8px',
          minHeight: level === 0 ? 56 : 40,
          pl: paddingLeft,
          pr: 2,
          justifyContent: isMenuOpen ? 'initial' : 'center',
          '&.Mui-selected': getSelectedStyles(isSelected, hasChildren),
          '&.Mui-selected:hover': getSelectedHoverStyles(isSelected)
        }}
      >
        <NavigationItemIcon
          alignIconWithTopLevel={item.alignIconWithTopLevel}
          icon={item.icon}
          level={level}
          paddingLeftNumber={paddingLeftNumber}
        />

        <ListItemText
          primary={
            <NavigationItemPrimary
              hasChildren={hasChildren}
              isMenuOpen={isMenuOpen}
              isSelected={isSelected}
              item={item}
              level={level}
              paddingLeftNumber={paddingLeftNumber}
              shouldBeOpen={shouldBeOpen}
            />
          }
        />
      </ListItemButton>

      <NavigationItemChildren
        fullPath={fullPath}
        hasChildren={hasChildren}
        isMenuOpen={isMenuOpen}
        items={item.children}
        level={level}
        shouldBeOpen={shouldBeOpen}
      />
    </ListItem>
  );
};

export default NavigationItem;
